import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "../modals/ResourceViewerModal.module.css";
import qrstyles from "./QuestionnaireResponseCardExtended.module.css";

import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import FemaleProvider from "../assets/Doctor_Female.jpg";
import MaleProvider from "../assets/Doctor_Male_96.png";
import DefaultProvider from "../assets/Doctor_Male_96.png";

import { useStore } from "react-redux";
import { fetchPatientByReference } from "../services/patientService";
import { fetchProviderByReference } from "../services/providerService";
import { fetchEncounterByReference } from "../services/encounterService";
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";
import { fetchAttachment } from "../utils/fetchAttachment";
import { getIconForContentType } from "../utils/getIconForContentType";
import PDFViewer from "../utils/PDFViewer";

/* -------------------- Helpers -------------------- */

const SectionHeader = ({ children }) => (
  <div className={qrstyles.sectionHeader}>{children}</div>
);

const Field = ({ label, children }) => (
  <div className="mb-1">
    <strong>{label}:</strong>{" "}
    {children ?? <span className="text-muted">N/A</span>}
  </div>
);

const renderValue = (value) => {
  if (value === null || value === undefined)
    return <span className="text-muted">N/A</span>;

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value.toString();
  }

  if (value.code || value.display || value.text) {
    return value.text || value.display || value.code;
  }

  if (value.reference) {
    return value.display || value.reference;
  }

  return <span className="text-muted">Unsupported value</span>;
};

/* -------------------- Recursive Item Renderer -------------------- */

const QuestionnaireItem = ({ item }) => {
  return (
    <div className={qrstyles.itemBlock}>
      {Array.isArray(item.answer) && item.answer.length > 0 ? (
        <div className={qrstyles.questionAnswerRow}>
          {item.text && <strong className={qrstyles.questionText}>{item.text}</strong>}
          <span className={qrstyles.answerSpacer}>:</span>
          <div className={qrstyles.answerInline}>
            {item.answer.map((a, idx) => {
              const valueKey = Object.keys(a).find(k => k.startsWith("value"));
              return (
                <span key={idx} className={qrstyles.answerValue}>
                  {renderValue(a[valueKey])}
                  {idx < item.answer.length - 1 && ", "}
                </span>
              );
            })}
          </div>
        </div>
      ) : item.text ? (
        <div className={qrstyles.questionText}>{item.text}</div>
      ) : null}

      {Array.isArray(item.item) && item.item.length > 0 && (
        <div className={qrstyles.subItems}>
          {item.item.map((child, idx) => (
            <QuestionnaireItem key={idx} item={child} />
          ))}
        </div>
      )}
    </div>
  );
};

/* -------------------- Attachment Normalizer -------------------- */

const normalizeAttachmentsFromQuestionnaire = (resource) => {
  const attachments = [];
  if (!resource?.item) return attachments;

  const extract = (item) => {
    if (!item) return;

    if (Array.isArray(item.answer)) {
      item.answer.forEach((a) => {
        if (a.valueAttachment) {
          attachments.push({
            ...a.valueAttachment,
            title: a.valueAttachment.title || "attachment",
            contentType: a.valueAttachment.contentType || "application/octet-stream",
          });
        }
      });
    }

    if (Array.isArray(item.item)) {
      item.item.forEach(extract);
    }
  };

  resource.item.forEach(extract);
  return attachments;
};

/* -------------------- Main Component -------------------- */

const QuestionnaireResponseExtendedCard = ({ resource, handleShowNested, fhirClient }) => {
  const store = useStore();
  const [subject, setSubject] = useState(null);
  const [subjectName, setSubjectName] = useState(null);

  const [author, setAuthor] = useState(null);
  const [authorName, setAuthorName] = useState(null);

  const [encounter, setEncounter] = useState(null);
  const [encounterName, setEncounterName] = useState(null);

  const [questionnaire, setQuestionnaire] = useState(null);

  const [attachments, setAttachments] = useState([]);

  // Modal state for viewing attachments
  const [showModal, setShowModal] = useState(false);
  const [modalBlobUrl, setModalBlobUrl] = useState("");
  const [modalType, setModalType] = useState("generic");
  const [modalTitle, setModalTitle] = useState("");

  useEffect(() => {
    if (!resource || !fhirClient) return;

    hydrateFhirGraph(resource, fhirClient, store);

    const load = async () => {
      if (resource.subject?.reference) {
        const p = await fetchPatientByReference(resource.subject.reference, fhirClient, store);
        setSubject(p);
        setSubjectName(resolveName(p));
      }

      if (resource.author?.reference) {
        const [type] = resource.author.reference.split("/");
        let resolved = null;
        if (type === "Practitioner") {
          resolved = await fetchProviderByReference(resource.author.reference, fhirClient, store);
        } else if (type === "Patient") {
          resolved = await fetchPatientByReference(resource.author.reference, fhirClient, store);
        }
        setAuthor(resolved);
        setAuthorName(resolveName(resolved));
      }

      if (resource.encounter?.reference) {
        const e = await fetchEncounterByReference(resource.encounter.reference, fhirClient, store);
        setEncounter(e);
        setEncounterName(resolveName(e));
      }

      if (resource.questionnaire) {
        setQuestionnaire(resource.questionnaire);
      }

      // Normalize attachments
      const normalized = normalizeAttachmentsFromQuestionnaire(resource);
      setAttachments(normalized);
    };

    load();
  }, [resource, fhirClient, store]);

  const patientPhotoUrl =
    subject?.photo?.[0]?.url ||
    (subject?.gender === "female" ? FemalePatient : MalePatient);

  const authorPhotoUrl =
    author?.photo?.[0]?.url ||
    (author?.gender === "female" ? FemaleProvider : MaleProvider);

  /* -------------------- Render -------------------- */
  return (
    <div className="p-4 border rounded shadow-sm bg-white">

      {/* HEADER */}
      <div className={qrstyles.headerRow}>
        {/* Patient */}
        <div className={qrstyles.entityBlock}>
          <button className={qrstyles.entityImageButton} onClick={() => handleShowNested?.(subject, subjectName)}>
            <img src={patientPhotoUrl} alt="Patient" className={qrstyles.avatar} />
          </button>
          <div>
            <Field label="Patient">{subjectName}</Field>
            <Field label="Birth Date">{subject?.birthDate}</Field>
          </div>
        </div>

        {/* Author */}
        <div className={qrstyles.entityBlock}>
          <button className={qrstyles.entityImageButton} onClick={() => handleShowNested?.(author, authorName)}>
            <img src={authorPhotoUrl || DefaultProvider} alt="Author" className={qrstyles.avatar} />
          </button>
          <div>
            <Field label="Author">{authorName}</Field>
            <Field label="Active">{author ? (author.active ? "Yes" : "No") : "N/A"}</Field>
          </div>
        </div>
      </div>

      {/* IDENTIFICATION */}
      <SectionHeader>Identification</SectionHeader>
      <div className={styles.indented}>
        <Field label="Response ID">{resource.id}</Field>
        <Field label="Status">{resource.status}</Field>
        <Field label="Questionnaire">{questionnaire ? renderReference(questionnaire, questionnaire, handleShowNested) : "N/A"}</Field>
        {resource.identifier?.map((id, idx) => (
          <div key={idx} className="ps-3 text-muted">
            {id.system} — {id.value}
          </div>
        ))}
      </div>

      {/* TIMING */}
      <SectionHeader>Timing</SectionHeader>
      <div className={styles.indented}>
        <Field label="Authored">{resource.authored}</Field>
        <Field label="Encounter">
          {resource.encounter?.reference
            ? renderReference(resource.encounter.reference, encounterName, handleShowNested)
            : "None"}
        </Field>
      </div>

      {/* RESPONSES */}
      <SectionHeader>Responses</SectionHeader>
      <div className={styles.indented}>
        {Array.isArray(resource.item) && resource.item.length > 0 ? (
          resource.item.map((item, idx) => <QuestionnaireItem key={idx} item={item} />)
        ) : (
          <div className="text-muted">No responses</div>
        )}
      </div>

      {/* ATTACHMENTS */}
      {attachments.length > 0 && (
        <>
          <SectionHeader>Attachments</SectionHeader>
          <div>
            {attachments.map((att, idx) => (
              <div key={idx} className="border rounded p-2 mb-2 d-flex align-items-start">
                <div className="me-2">{getIconForContentType(att.contentType, "Media")}</div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="fw-bold text-truncate me-3" style={{ maxWidth: "70%" }}>
                      {att.title} <span className="text-muted">({att.contentType || "N/A"})</span>
                    </div>
                    <div className="d-flex">
                      {/* OPEN */}
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={async () => {
                        const fetched = await fetchAttachment(att, fhirClient);
                        if (!fetched?.blob) return alert("Failed to open attachment.");
                        const url = URL.createObjectURL(fetched.blob);
                        setModalBlobUrl(url);
                        setModalTitle(att.title);
                        setShowModal(true);

                        if (att.contentType?.includes("pdf")) setModalType("pdf");
                        else if (att.contentType?.startsWith("image/")) setModalType("image");
                        else if (att.contentType?.startsWith("audio/")) setModalType("audio");
                        else if (att.contentType?.startsWith("video/")) setModalType("video");
                        else setModalType("generic");
                      }}>Open</button>

                      {/* DOWNLOAD */}
                      <button className="btn btn-sm btn-outline-primary" onClick={async () => {
                        const fetched = await fetchAttachment(att, fhirClient);
                        if (!fetched?.blob) return alert("Failed to download attachment.");
                        const blobUrl = URL.createObjectURL(fetched.blob);
                        const a = document.createElement("a");
                        a.href = blobUrl;
                        a.download = att.title.replace(/\s+/g, "_");
                        a.click();
                        URL.revokeObjectURL(blobUrl);
                      }}>Download</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* METADATA */}
      <SectionHeader>Metadata</SectionHeader>
      <div className={styles.indented}>
        <Field label="Language">{resource.language}</Field>
        <Field label="Last Updated">{resource.meta?.lastUpdated}</Field>
        <Field label="Version">{resource.meta?.versionId}</Field>
      </div>

      {/* MODAL */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          if (modalBlobUrl) URL.revokeObjectURL(modalBlobUrl);
          setModalBlobUrl("");
        }}
        size="xl"
        fullscreen="md-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh" }}>
          {modalType === "pdf" && <PDFViewer blobUrl={modalBlobUrl} />}
          {modalType === "image" && <img src={modalBlobUrl} alt={modalTitle} style={{ maxWidth: "100%", maxHeight: "100%" }} />}
          {modalType === "video" && <video src={modalBlobUrl} controls style={{ width: "100%", height: "100%" }} />}
          {modalType === "audio" && <audio src={modalBlobUrl} controls style={{ width: "100%" }} />}
          {modalType === "generic" && <iframe src={modalBlobUrl} title={modalTitle} style={{ width: "100%", height: "100%", border: "none" }} />}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QuestionnaireResponseExtendedCard;
